import type { UseQueryResult } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

import type { ApiError } from "@/api/client";
import { queryKeys, type SectionConfig, type SectionSlug } from "@/api/hooks";
import {
  useAssetVersionsQueries,
  useLibraryPlacementRegistryQuery,
  type LibraryAssetList,
  type ProjectAdoptions,
} from "@/api/library";
import { isTerminalQueryError } from "@/api/queryClient";
import {
  useRetryPausedQueries,
  useRetryPausedQuery,
} from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import {
  activationLaneForSection,
  adoptionLock,
  adoptionRefusal,
  type AdoptionDecision,
} from "@/lib/adoptions";
import {
  deriveSectionAdoption,
  librarySectionForRailSlug,
  sectionAdoptionCandidates,
} from "@/lib/sectionAdoption";
import { ProjectSectionPane } from "@/ui/project/ProjectSectionPane";
import { DeniedState, UnavailableState } from "@/ui/states";

import { AdoptFromLibraryAction } from "./AdoptFromLibraryAction";
import { AdoptionDecisionDialog } from "./AdoptionDecisionDialog";
import {
  hasSectionAdoptedItemActions,
  RetryNotice,
  SectionAdoptedItemActions,
  SectionAdoptionBlock,
  type SectionAdoptionActions,
  type SectionAdoptionCandidate,
} from "./SectionAdoptionBlock";

/**
 * The query wiring behind one section's adoption block (#443).
 *
 * WHY THE THREE READS ARRIVE AS PROPS. `summary` renders TWO adoption views —
 * this section-scoped one and #373's whole-pin-set list — and they must not be
 * able to disagree. The only way to guarantee that is for both to observe ONE
 * `useProjectAdoptionsQuery`, so `ClinicalSummary` keeps holding it and passes
 * it down, exactly as it already passes it to `PinConsequences`. The same
 * applies to the catalog and the section projection: `SectionDiagnostics` and
 * `ClinicalSummary` each already hold their section query, and a second one
 * here would be a second cache observer of the same key for no gain.
 *
 * WHAT IT DOES READ ITSELF is the version history of the adopted assets, and
 * only because nothing else can answer "last relevant change": neither
 * `GET /adoptions` nor a section projection carries a timestamp. The read is
 * per adopted asset, keyed under the library root, and shared with the asset
 * screen's own history query.
 *
 * A FAILURE IS NEVER "ADOPTS NOTHING". Each query's failure is translated into
 * `null` for the derivation, which is a different value from an empty list and
 * renders as `unavailable` with a retry. That is the ticket's own criterion,
 * and it is the reason this component passes `null` rather than `?? []`.
 *
 * IT ALSO OWNS THE DECISION STATE (#489). The ticket moves the adopt /
 * change-pin ACTION beside the evidence #443 already put in every section, and
 * this is the layer that can hold it: `SectionAdoptionBlock` is prop-driven
 * precisely so it can be exercised in states no live lane produces, and a
 * component that opened its own dialog would have to read the pin set to do it.
 * So the arrangement is `ProjectAdoptions`' exactly — the query holder keeps
 * `decision` and mounts `AdoptionDecisionDialog`, the presentational component
 * calls `onDecide` — and `AdoptionDecisionDialog` is reused UNCHANGED, so the
 * whole-set `PUT /adoptions` replacement and its confirmation are the same code
 * path #372 shipped rather than a second one that could disagree with it.
 *
 * ONE DECISION PER ADOPTED ITEM, AND THE CANONICAL ROW HOLDS IT (#570 round 2).
 * On the six review slugs the bounded row now carries the Adopt/ellipsis
 * actions the amendment requires, so an adopted item was left with TWO
 * existing-item controls — the evidence block's Update/Unpin and the row's own.
 * Not merely duplicated buttons over one view model: the block's target version
 * is the CATALOG's `latestVersion`, joined in `deriveSectionAdoption`, while
 * the row's is the SUMMARY's own for an adopted item. A publish landing between
 * those two responses could offer "Update … to v6" and "Adopt v5" for a single
 * pin — two authorities for one version-sensitive clinical write.
 *
 * #909 makes that separation structural as well as logical: the rows are this
 * panel's `children`, inside `ProjectSectionPane`, and the block is the pane's
 * `Details`. The rule is unchanged and so is `canonicalItemIds`.
 *
 * `canonicalItemIds` is how the caller says which items already have that row,
 * and this panel then offers no second control for them — neither the
 * Update/Unpin above nor the Adopt below.
 *
 * AND THE ADOPT-NEW LIST IS FILTERED BY THE SAME SET (#570 round 3). Round 2
 * applied the ids to the adopted items only, on the reading that a candidate —
 * an asset this project does not pin — can never reach a bounded row. The
 * service says otherwise, and says it deliberately: `assemble_section_summary`
 * emits every UNADOPTED seed-manifest entry of this section's family as a
 * `supplyState: "base"` row, and `SectionReview.itemActions` then gives that row
 * `Adopt v<N>` from the catalog. Such an asset is by construction unpinned and
 * of an adoptable family, so `sectionAdoptionCandidates` listed it as well —
 * panel `Pin <item>` above row `Adopt v<N>`, one asset, two controls, on a
 * successful BG sets, Main concern, Rules or Safety section.
 *
 * WHAT IT STILL DOES NOT SUPPRESS is everything the bounded response does not
 * name, which is why the prop is a set of ids rather than a boolean. The
 * candidates that survive it are not a residue: a library asset the packaged
 * seed catalog does not carry reaches no base row at all; neither does any
 * candidate on Pathways or Alerts, whose refs-only contract emits no base rows;
 * and a section that already adopts one item of a single-source family
 * suppresses that family's base rows while its other assets stay adoptable.
 * Filtering the list wholesale, or by family, would delete each of those. So
 * does the fallback the set keeps for an item no row covers: the same
 * Update/Unpin controls for any adopted item the review body did NOT render — a
 * summary that failed, composed nothing, or simply does not name the item, and
 * every slug that has no review body (the Clinical summary passes no set at
 * all). The evidence this block renders is untouched in every case: only the
 * controls move.
 *
 * A TERMINAL FAILURE IS NEVER CACHED DATA EITHER. TanStack Query keeps
 * `result.data` when a REFETCH fails, so a query that answered once and is then
 * refused keeps serving its last success to anything reading `.data` — which,
 * on this block, is a clinician's asset identity, adopted version and pin set.
 * `queryClient.ts` states the rule the whole shell follows: cached data carries
 * a transient outage, and 401/403/404/501 and a terminal 409 fail closed
 * instead. So each read is gated on its CURRENT error classification below, and
 * a terminal one takes the panel to a denial rather than to a state derived
 * from what the caller could see a moment ago.
 */
export function SectionAdoptionPanel({
  projectId,
  section,
  sectionLabel,
  sectionQuery,
  adoptions,
  catalog,
  canonicalItemIds,
  libraryLinkedItemIds,
  controls,
  children,
}: {
  projectId: string;
  section: SectionSlug;
  sectionLabel: string;
  /** The caller's section projection. Never re-fetched here. */
  sectionQuery: UseQueryResult<SectionConfig>;
  /** The caller's single pin-set read. Never re-fetched here. */
  adoptions: UseQueryResult<ProjectAdoptions>;
  /** The caller's catalog read, archived rows included. */
  catalog: UseQueryResult<LibraryAssetList>;
  /**
   * Asset ids whose adoption decision a canonical review row already carries,
   * or absent on a surface that renders no such rows.
   *
   * BOTH HALVES OF THIS BLOCK READ IT — the adopted items above and the
   * adopt-new candidates below — because a bounded row speaks for either kind:
   * the service reports an adopted item as a `supplyState: "adopted"` row and an
   * unadopted base-pack one as a `"base"` row, and both rows carry the decision.
   * An id here therefore suppresses whichever of the two controls this block
   * would otherwise have offered for that asset, and only that asset's — and,
   * since #615, the item's own `View in Clinical library`, which the row's
   * menu already carries. The set reaches the block on its own prop for that:
   * the link is not gated on the pin-set read, so folding coverage into
   * `actions` alone would leave the duplicate standing exactly when `actions`
   * is withheld.
   *
   * ABSENT AND EMPTY MEAN THE SAME THING HERE — no row speaks for any item, so
   * this block keeps every control it had. They are still worth distinguishing
   * in a caller: the Clinical summary has no review body at all, while a review
   * slug whose bounded read failed has one that rendered nothing.
   */
  canonicalItemIds?: ReadonlySet<string>;
  /**
   * Asset ids whose exact `View in Clinical library` another surface on the
   * page already carries WITHOUT carrying their decision (#615), or absent.
   *
   * The Clinical summary's case: its whole-pin-set cards (`PinConsequences`)
   * link every pin and write nothing, so a pinned Summary prompt would be
   * linked twice on that page — here, as the section's adopted item, and as
   * its card — with every control still belonging here. Naming those ids in
   * `canonicalItemIds` instead would withhold the update/remove controls too,
   * leaving the item with no control anywhere; this set withholds ONLY the
   * link. Absent and empty mean the same thing, as above.
   */
  libraryLinkedItemIds?: ReadonlySet<string>;
  /**
   * Section-scoped controls above the rows, or absent (#909).
   *
   * Passed straight to `ProjectSectionPane`, whose header documents what the
   * slot is for: the typed settings waves 2-5 add (BG profile, Closing,
   * Speech, Summary policy) arrive here rather than by restructuring this
   * panel or the routes above it.
   */
  controls?: ReactNode;
  /**
   * The section body — its compact rows (#909).
   *
   * The caller keeps owning what the rows ARE: `SectionDiagnostics` passes
   * `SectionReview`'s bounded rows, the Clinical summary passes its own body.
   * What this panel owns is where they sit relative to the adoption action
   * above them and the evidence below.
   */
  children?: ReactNode;
}) {
  const write = useCapability("draft.write");
  // The SERVED cardinality policy (#634). A pin on a family the server bounds
  // at one is a replacement, and `AdoptionDecisionDialog` cannot describe which
  // pin it displaces without this document — so an unread policy becomes a
  // STATED refusal on the adopt control rather than a control whose only
  // outcome is that refusal (ADR 0001). Removal is deliberately not gated on
  // it: an un-pin is the recovery path out of every degraded state (#612).
  const policyUnread = !useLibraryPlacementRegistryQuery().isSuccess;
  const [decision, setDecision] = useState<AdoptionDecision | null>(null);

  // The one terminal failure this panel answers to, in the order the reads
  // matter: the projection carries the identity, the catalog the head version,
  // the pin set the cross-check. First match wins because the surface renders
  // one statement, and the first is the one that invalidates the most.
  const terminal =
    terminalErrorOf(sectionQuery) ??
    terminalErrorOf(catalog) ??
    terminalErrorOf(adoptions);

  // Gated on the CURRENT classification rather than on the presence of data: a
  // refused refetch leaves the previous success in `.data`, and rendering it
  // would be this block telling a clinician what a project adopts after the
  // service has said they may no longer see it.
  const catalogItems = isTerminal(catalog)
    ? null
    : (catalog.data?.items ?? null);
  // THE SAME ROWS, GATED HARDER, AND ONLY FOR WRITES (Codex, #909 round 1). The
  // EVIDENCE below may read a cached catalog through a transient failure — that
  // is what the retry is for, and blanking an adopted item's name because a
  // refetch failed would be worse than stating it. A WRITE may not:
  // `adoptionDecisionReady` gates the confirmation on `catalog.isSuccess`, so a
  // candidate offered over a non-current catalog opens a dialog that can only
  // refuse, which is the affordance ADR 0001 forbids.
  const writableCatalogItems = catalog.isSuccess
    ? (catalog.data?.items ?? null)
    : null;
  const pins = isTerminal(adoptions) ? null : (adoptions.data?.items ?? null);
  const pinSetVersion = isTerminal(adoptions)
    ? null
    : (adoptions.data?.adoptionsVersion ?? null);

  const adoption = deriveSectionAdoption({
    section: isTerminal(sectionQuery) ? null : (sectionQuery.data ?? null),
    catalog: catalogItems,
    pins,
    pinSetVersion,
  });

  // Derived before the branches below so the hook's argument does not depend on
  // which one renders: a conditional call would be a conditional hook, and an
  // empty list is a legal argument that fetches nothing.
  const history = useAssetVersionsQueries(
    adoption.items.map((item) => item.assetId),
  );

  // `cancelQueries` then `refetchQueries`, never a bare `refetch()`. A PAUSED
  // query answers `refetch()` by returning the paused retryer's own promise
  // without firing anything (#215) — which is exactly the state the block's
  // recovery is offered in, so the obvious call is the one that does nothing.
  const retrySection = useRetryPausedQuery(
    queryKeys.section(projectId, section),
  );
  const retryCatalog = useRetryPausedQuery(queryKeys.libraryAssets(true));
  const retryAdoptions = useRetryPausedQuery(
    queryKeys.projectAdoptions(projectId),
  );
  // The history keys are DATA — one per adopted asset — so they are refetched
  // through the set-shaped form. Omitting them is what left a transient
  // version-history failure with no recovery at all: the button existed, and
  // reached three keys that had not failed.
  const retryHistory = useRetryPausedQueries(
    history.retryableAssetIds.map((assetId) =>
      queryKeys.assetVersions(assetId),
    ),
  );

  // Still being read is not the same answer as could not be read, and a PAUSED
  // read is the second rather than the first (#215, refined by #327): a held
  // retry keeps its query `pending` with nothing in flight, so "reading…" would
  // sit there indefinitely in a background tab over a failure the service had
  // already answered. `ProjectionNotice` draws exactly this line and this
  // follows it — a paused query falls through to the block below, where the
  // absent data renders `unavailable` with a retry.
  const stillReading =
    (sectionQuery.isPending && !sectionQuery.isPaused) ||
    (catalog.isPending && !catalog.isPaused) ||
    (adoptions.isPending && !adoptions.isPaused);

  // THE TWO DEGRADED STATES STAY VISIBLE, AND STOP BEING AN EARLY RETURN
  // (#909). They are statements about the ADOPTION READS — the projection, the
  // catalog, the pin set — and the section's rows come from a different read
  // that answers, fails and recovers on its own. Returning early replaced the
  // whole pane with a denial or a skeleton, which on a shared shell would take
  // the rows and the section's other controls down with it.
  //
  // NEITHER IS `details` CONTENT. A refusal a clinical author has to expand a
  // disclosure to discover is a hidden refusal, which is the thing this ticket
  // forbids; and an `aria-live` progress line inside a closed `<details>`
  // announces to nobody. Both render in the `controls` slot, above the rows,
  // where the pane puts what an author must read before the list. The
  // disclosure is for evidence behind a state that HAS resolved.
  if (terminal !== null) {
    // Ahead of the pending branch: a query refused mid-session can be pending
    // again on the next mount while its terminal error still stands, and a
    // skeleton over a denial is the cached-data defect in a slower form.
    //
    // NO PRIMARY ACTION HERE. A refused read is exactly the state in which an
    // adopt control could only open a picker over rows the service has said
    // this account may not see.
    return (
      <ProjectSectionPane
        sectionLabel={sectionLabel}
        controls={
          <>
            <TerminalDetail error={terminal} sectionLabel={sectionLabel} />
            {controls}
          </>
        }
      >
        {children}
      </ProjectSectionPane>
    );
  }

  if (stillReading) {
    // A quiet line rather than `QueryPendingState`, deliberately. That
    // component's answered-failure branch renders a "Try again" control, and
    // the surfaces this panel sits on already render one for the SAME section
    // query — two identically named buttons for one failure, which is
    // ambiguous to a screen reader and to a clinician. The block's own
    // recovery below is named "Read it again" and is the only one this panel
    // contributes.
    return (
      <ProjectSectionPane
        sectionLabel={sectionLabel}
        controls={
          <>
            <p
              role="status"
              aria-live="polite"
              className="text-xs text-muted-foreground"
            >
              Reading what supplies {sectionLabel}, and what this project has
              adopted…
            </p>
            {controls}
          </>
        }
      >
        {children}
      </ProjectSectionPane>
    );
  }

  // WHICH READS A RETRY WOULD ACTUALLY REACH. The headline state is not the
  // answer: a transient `GET /adoptions` failure beside a healthy projection
  // and catalog leaves the block reading `current` or `outdated`, which used to
  // render no recovery at all — the pin set stayed unread until the clinician
  // left the route. Recovery follows the failed READS, in every state.
  const failedReads: string[] = [];
  if (isRetryable(sectionQuery)) {
    failedReads.push("this section's effective configuration");
  }
  if (isRetryable(catalog)) {
    failedReads.push("the shared library catalog");
  }
  if (isRetryable(adoptions)) {
    failedReads.push("this project's pin set");
  }
  if (history.retryableAssetIds.length > 0) {
    failedReads.push(
      history.retryableAssetIds.length === 1
        ? "the version history of one adopted item"
        : `the version history of ${history.retryableAssetIds.length} adopted items`,
    );
  }

  // WHY THE ACTIONS ARE GATED ON `pinSetRead`. Every decision this offers is a
  // WHOLE-SET replacement: `adoptionImpact` rewrites the pin set with one entry
  // changed, so a control offered over an unread set would open a dialog that
  // can only fail closed. `AdoptionDecisionDialog` does fail closed there — it
  // is the same rule `LifecycleDialog` and `MatrixPinning` apply — but offering
  // a control whose single outcome is that refusal is the affordance #4 forbids.
  // A converging section is withheld for the same reason: its item list belongs
  // to an older pin-set token, so an Update/Unpin beside it would be a decision
  // over a read the UI is explicitly waiting to settle.
  const actions: SectionAdoptionActions | null =
    adoption.pinSetRead && adoption.state !== "converging"
      ? {
          onDecide: setDecision,
          // Filtered, not emptied. An item whose canonical row carries the
          // decision is left out of the map, and `AdoptedItem` renders no
          // controls for an item it cannot find refusals for — so the
          // consolidation costs this block nothing but the duplicate buttons,
          // and every item without a row keeps both of its own.
          refusalsByAssetId: new Map(
            adoption.items
              .filter((item) => !canonicalItemIds?.has(item.assetId))
              .map((item) => [
                item.assetId,
                {
                  // `held` separates the two things a `pin` decision means,
                  // exactly as `ProjectAdoptions` reads it off its own pin set: on
                  // an item this project already pins it is a MOVE, on one it does
                  // not it is an ADD, and `adoptionRefusal` refuses a locked MOVE
                  // only.
                  pin: adoptionRefusal("pin", {
                    asset: item.asset,
                    lock: item.effectiveLock,
                    held: item.pinnedVersion !== null,
                    missingWriteReason: write.reason,
                    policyUnread,
                  }),
                  unpin: adoptionRefusal("unpin", {
                    asset: item.asset,
                    lock: item.effectiveLock,
                    held: true,
                    missingWriteReason: write.reason,
                    // NEVER the policy on a removal: the un-pin is the recovery path
                    // out of every degraded state, and no read may take it away
                    // (#612).
                    policyUnread: false,
                  }),
                },
              ]),
          ),
        }
      : null;

  // THE ADOPT-NEW HALF, now the PANE'S TOP-RIGHT ACTION (#489, moved by #909).
  // It cannot come from `adoption.items`: an item this project has never pinned
  // is named by no FIELD ORIGIN, no matrix ref and no pathway ref, so the
  // section projection cannot report it. The candidates are the catalog's,
  // filtered to this section's families and to what the pin set does not
  // already hold — the same gated reads the evidence below uses, so the action
  // and the evidence cannot disagree.
  //
  // `held: false`, always. Every candidate is by construction an asset this
  // project does not pin, so this is an ADD; `adoptionRefusal` refuses a locked
  // MOVE and never a locked add, which is the rule the `/adoptions` surface
  // applied to the identical list.
  //
  // `null` SURVIVES THE MAPPING, and that is the whole of #511's second round.
  // The gate is `pinSetRead`, which says nothing about the CATALOG — so a
  // paused catalog with nothing cached still reaches here, and mapping it to
  // `[]` made the surface announce that the library offered nothing further
  // while the recovery notice beside it said the catalog had not been read. A
  // failure is never "adopts nothing", and `null` is not an empty list: it is
  // passed through, and `AdoptFromLibraryAction` states it on the control.
  //
  // GATED ON `actions` FOR THE SAME REASON THE OTHER HALF IS: every adoption is
  // a whole-set replacement, so a control offered over an unread pin set could
  // only open a confirmation that fails closed.
  const candidates: readonly SectionAdoptionCandidate[] | null | undefined =
    actions === null
      ? undefined
      : (sectionAdoptionCandidates({
          section,
          // `writableCatalogItems`, never the evidence's cached rows: see the
          // note where it is derived.
          catalog: writableCatalogItems,
          pins,
        })
          // BY ID, never by family and never wholesale (#570 round 3). A
          // BOUNDED ROW ALREADY OFFERS THIS EXACT ADOPTION when the summary
          // named the asset — which for an unpinned one means the service
          // rendered it as a base-pack row — and the row is the canonical place
          // the decision is taken. An id absent from that set has no row
          // anywhere on the screen, so this list is the only place it can be
          // offered and it stays; suppressing this section's whole family, or
          // the list itself, would take the capability back out.
          //
          // The short-circuit is deliberate: `null` — the catalog or the pin
          // set unread — must survive the whole chain rather than becoming an
          // empty list, which is the distinction the paragraph above draws.
          // Optional chaining short-circuits every call that follows it, so the
          // filter and the mapping are both skipped and `?? null` still sees
          // `undefined`.
          ?.filter((asset) => !canonicalItemIds?.has(asset.id))
          .map((asset) => ({
            asset,
            refusal: adoptionRefusal("pin", {
              asset,
              lock: adoptionLock(asset, undefined),
              held: false,
              missingWriteReason: write.reason,
              policyUnread,
            }),
          })) ?? null);

  // What the block withholds its item LINK for: the ids a canonical row speaks
  // for (its menu carries the link beside the decision) and the ids another
  // surface links without deciding. The two sets differ only in what ELSE they
  // withhold — the controls, folded above from `canonicalItemIds` alone.
  const blockLinkedItemIds =
    canonicalItemIds || libraryLinkedItemIds
      ? new Set([...(canonicalItemIds ?? []), ...(libraryLinkedItemIds ?? [])])
      : undefined;

  // WHAT STANDS ABOVE THE ROWS, IN THE OPEN (#909 round 2). Three things, in
  // the order an author meets them: the section's own typed controls, the
  // recovery for any read that failed, and the decisions on adopted items that
  // no canonical review row speaks for. Each was inside the disclosure after
  // round 1, and each is something to ACT on rather than evidence to read —
  // the recovery because a hidden retry leaves a stale surface with no way
  // out, the item decisions because on the Clinical summary they are the only
  // ones there are (Codex).
  // GATED ON WHAT WOULD ACTUALLY RENDER (Codex, round 2). `actions !== null`
  // says a decision COULD be offered, not that one is: on the six review slugs
  // `canonicalItemIds` empties the refusal map and the list returns `null`,
  // while the fragment around it stays truthy — so the pane rendered an empty
  // controls wrapper that took a margin from its parent and opened a blank gap
  // above the compact rows.
  const itemActions =
    actions !== null && hasSectionAdoptedItemActions(adoption.items, actions);
  const paneControls =
    controls || failedReads.length > 0 || itemActions ? (
      <>
        {controls}
        {failedReads.length > 0 ? (
          <RetryNotice
            failedReads={failedReads}
            onRetry={() => {
              if (isRetryable(sectionQuery)) {
                retrySection();
              }
              if (isRetryable(catalog)) {
                retryCatalog();
              }
              if (isRetryable(adoptions)) {
                retryAdoptions();
              }
              retryHistory();
            }}
          />
        ) : null}
        {actions && itemActions ? (
          <SectionAdoptedItemActions items={adoption.items} actions={actions} />
        ) : null}
      </>
    ) : undefined;

  return (
    <>
      <ProjectSectionPane
        sectionLabel={sectionLabel}
        {...(paneControls ? { controls: paneControls } : {})}
        // LOVABLE'S TOP-RIGHT ACTION (#909), offered wherever the pin set was
        // read — including on a section that pins nothing, which is the empty
        // case the ticket asks to keep discoverable. `candidates` is
        // `undefined` only where `actions` is null, and that is the one state
        // with no control at all.
        {...(candidates !== undefined
          ? {
              primaryAction: (
                <AdoptFromLibraryAction
                  sectionLabel={sectionLabel}
                  candidates={candidates}
                  onDecide={setDecision}
                  // Which write a `Pin …` performs — the Pathways section's
                  // adoptions go through the server-owned activation (#749),
                  // the Main concern's through its own (#634) — read from the
                  // section's adoptable families through the SAME map the
                  // decision dialog chooses its writer by, so the sentence
                  // above the list cannot describe a write the confirmation
                  // does not perform.
                  activation={activationLaneForSection(
                    librarySectionForRailSlug(section),
                  )}
                />
              ),
            }
          : {})}
        details={
          <SectionAdoptionBlock
            projectId={projectId}
            sectionLabel={sectionLabel}
            librarySection={librarySectionForRailSlug(section)}
            adoption={adoption}
            versionsByAssetId={history.byAssetId}
            historyPending={history.isPending}
            historyFailedAssetIds={history.failedAssetIds}
            // NAMED, but not RECOVERED here since #909 round 2: the block
            // still says which reads did not answer, and the one `Read it
            // again` stands above the rows with the other things an author
            // acts on. Two of them — one visible, one collapsed — would be the
            // ambiguity this notice was named to avoid.
            failedReads={failedReads}
            {...(blockLinkedItemIds
              ? { libraryLinkedItemIds: blockLinkedItemIds }
              : {})}
          />
        }
      >
        {children}
      </ProjectSectionPane>

      {decision ? (
        // #372's confirmation, unchanged and mounted from here: it computes the
        // whole-set replacement, states which pin moves between which exact
        // versions and which others are written back untouched, and submits
        // under the `adoptionsVersion` it LOADED. Nothing on this surface writes
        // without passing through it.
        <AdoptionDecisionDialog
          projectId={projectId}
          decision={decision}
          onClose={() => setDecision(null)}
        />
      ) : null}
    </>
  );
}

/** Whether this read's CURRENT error is one no retry can clear. */
function isTerminal(query: UseQueryResult<unknown>): boolean {
  return query.isError && isTerminalQueryError(query.error);
}

/** That error itself, for the surface that has to name it. */
function terminalErrorOf(query: UseQueryResult<unknown>): ApiError | null {
  return query.isError && isTerminalQueryError(query.error)
    ? query.error
    : null;
}

/**
 * Whether a retry of this read could change its answer.
 *
 * A paused read counts: its failure has not settled, and resuming it is exactly
 * what the control does (#215, #327). A terminal one never does.
 */
function isRetryable(query: UseQueryResult<unknown>): boolean {
  if (query.isPaused) {
    return true;
  }
  return query.isError && !isTerminalQueryError(query.error);
}

/**
 * A read this caller may not have, rendered as a refusal rather than as an
 * absence.
 *
 * The shared states, with NO action of their own. `QueryErrorState` renders
 * these same two components and adds a control — "Sign in again" on a 401 — and
 * the surfaces this panel sits on already route the same section error through
 * it. A second identically named control for one failure is the defect this
 * panel was already fixed for once; the section body below owns the recovery,
 * and this block says what it cannot show. Its title names the block's own
 * subject so the two statements read as two statements rather than as one
 * duplicated.
 */
function TerminalDetail({
  error,
  sectionLabel,
}: {
  error: ApiError;
  sectionLabel: string;
}) {
  if (error.isUnauthenticated) {
    return (
      <DeniedState
        title={`What supplies ${sectionLabel} is not shown`}
        description="Your session has expired, so this project's adoptions were not re-read. Signing in again restores them."
      />
    );
  }

  if (error.isForbidden) {
    return (
      <DeniedState
        title={`What supplies ${sectionLabel} is not shown`}
        description="You do not have access to this project's adoption detail. Ask a workspace administrator to grant it."
      />
    );
  }

  return (
    <UnavailableState
      title={`What supplies ${sectionLabel} is not shown`}
      description={
        error.isNotImplemented
          ? "The Editor Service accepts this request but has not implemented it yet, so what supplies this section cannot be established. There is nothing to retry."
          : "The Editor Service answered this project's adoption reads with a status no retry can clear, so what supplies this section is not shown. This is not a statement that the section adopts nothing."
      }
    />
  );
}
